import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Box, Tab, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import { TabContext, TabList } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { CalendarToday, Description } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import BillingOverview from "../components/BillingOverview";
import BillingClients from "../components/BillingClients";
import BillingInactiveClients from "../components/BillingInactiveClients";
import BillingRates from "../components/BillingRates";
import BillingInvoices from "../components/BillingInvoices";
import BillingFixedPricePayments from "../components/BillingFixedPricePayments";
import BillingFixedPriceSetupNeeded from "../components/BillingFixedPriceSetupNeeded";
import BillingCharges from "../components/BillingCharges";
import BillingEmployees from "../components/BillingEmployees";
import BillingEntities from "../components/BillingEntities";
import BillingRetainers from "../components/BillingRetainers";
import BillingUnbilled from "../components/BillingUnbilled";
import NewInvoiceDialog from "../components/NewInvoiceDialog";
import PageHeader from "../components/PageHeader";
import ActivitySummaryPicker from "../components/ActivitySummaryPicker";
import { generateInvoicesForMonth } from "../redux/ducks/invoices";
import dayjs from "dayjs";
import styled from "@emotion/styled";

const previousMonthStartDayjs = () => dayjs().startOf("month").subtract(1, "month");
const twelveMonthsAgoStartDayjs = () => dayjs().startOf("month").subtract(12, "month");
const todayStartDayjs = () => dayjs().startOf("day");

const ContainerBox = styled(Box)(({ theme }) => ({
    width: "100%",
    minWidth: 0,
    maxWidth: theme.breakpoints.values.xl,
    margin: "0 auto",
    padding: theme.spacing(2),
    overflowX: "clip",
    [theme.breakpoints.up("md")]: {
        padding: theme.spacing(3),
    },
}));

const HeaderActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    [theme.breakpoints.up('sm')]: {
        gap: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        '& > *': {
            flex: '1 1 0',
        },
    },
}));

const TabsBar = styled(Box)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTabs-root': {
        minWidth: 0,
    },
}));

const DueDateSection = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
}));

const DueDateHelper = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
}));

const DueDateResetButton = styled(Button)({
    alignSelf: "flex-start",
    minWidth: 0,
    paddingLeft: 0,
    paddingRight: 0,
    textTransform: "none",
});

export default function BillingDashboard() {
  const navigate = useNavigate();
  const { tab } = useParams();
  const dispatch = useDispatch();
  
  // Get tab from URL params, default to 'overview'
  const currentTab = tab || 'overview';
  const [tabValue, setTabValue] = useState(currentTab);
  const [newInvoiceDialogOpen, setNewInvoiceDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [monthPickerAnchorEl, setMonthPickerAnchorEl] = useState(null);
  const [monthPickerValue, setMonthPickerValue] = useState(previousMonthStartDayjs());
  const [dueDateValue, setDueDateValue] = useState(null);
  const [invoiceDateValue, setInvoiceDateValue] = useState(previousMonthStartDayjs().endOf("month"));
  const isMonthPickerOpen = Boolean(monthPickerAnchorEl);
  const monthPickerBounds = { min: twelveMonthsAgoStartDayjs(), max: previousMonthStartDayjs() };
  const earliestDueDate = todayStartDayjs();
  const isDueDateSet = Boolean(dueDateValue);
  const isDueDateInvalid = isDueDateSet && (!dayjs.isDayjs(dueDateValue) || !dueDateValue.isValid() || dueDateValue.startOf("day").isBefore(earliestDueDate));
  const isInvoiceDateInvalid = !invoiceDateValue || !dayjs.isDayjs(invoiceDateValue) || !invoiceDateValue.isValid();
  const isDueBeforeInvoice = isDueDateSet && !isDueDateInvalid && !isInvoiceDateInvalid && dueDateValue.startOf("day").isBefore(invoiceDateValue.startOf("day"));

  // Update tab value when URL changes (browser back/forward)
  useEffect(() => {
    setTabValue(currentTab);
  }, [currentTab]);

  const handleTabChange = (event, newValue) => {
    // Update URL without reloading the page
    navigate(`/billing/${newValue}`, { replace: true });
  };

  const openMonthPicker = (event) => {
    const month = previousMonthStartDayjs();
    setMonthPickerValue(month);
    setInvoiceDateValue(month.endOf("month"));
    setDueDateValue(null);
    setMonthPickerAnchorEl(event.currentTarget);
  };

  const changeMonth = (value) => {
    setMonthPickerValue(value);
    if (value && dayjs.isDayjs(value) && value.isValid()) setInvoiceDateValue(value.endOf("month"));
  };

  const closeMonthPicker = () => setMonthPickerAnchorEl(null);

  const submitMonthPicker = () => {
    if (!monthPickerValue || !dayjs.isDayjs(monthPickerValue) || !monthPickerValue.isValid()) return;
    if (isDueDateInvalid || isInvoiceDateInvalid || isDueBeforeInvoice) return;
    const month = monthPickerValue.format("YYYY-MM");
    const dueDate = isDueDateSet ? dueDateValue.format("YYYY-MM-DD") : undefined;
    const invoiceDate = invoiceDateValue.format("YYYY-MM-DD");
    closeMonthPicker();
    setIsGenerating(true);
    dispatch(generateInvoicesForMonth(month, () => {
      setIsGenerating(false);
    }, dueDate, invoiceDate));
  };

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  return (
    <ContainerBox>
      <PageHeader
        title="Billing Management"
        actions={
          <HeaderActions>
            <Button
              variant="contained"
              startIcon={<Description />}
              onClick={() => setNewInvoiceDialogOpen(true)}
            >
              New Invoice
            </Button>
            <Button
              variant="outlined"
              startIcon={isGenerating ? <CircularProgress size={16} /> : <CalendarToday />}
              onClick={openMonthPicker}
              disabled={isGenerating}
            >
              {isMobile ? 'Generate Invoices' : 'Generate Monthly Invoices'}
            </Button>
          </HeaderActions>
        }
      />

      <TabContext value={tabValue}>
        <TabsBar>
          <TabList
            onChange={handleTabChange}
            aria-label="billing management tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="Overview" value="overview" />
            <Tab label="Clients" value="clients" />
            <Tab label="Entities" value="entities" />
            <Tab label="Retainers" value="retainers" />
            <Tab label="Inactive" value="inactive" />
            <Tab label="Rates" value="rates" />
            <Tab label="Charges" value="charges" />
            <Tab label="Invoices" value="invoices" />
            <Tab label="Unbilled" value="unbilled" />
            <Tab label="FP Payments" value="payments" />
            <Tab label="Setup Needed" value="setup-needed" />
            <Tab label="Employees" value="employees" />
          </TabList>
        </TabsBar>
        <BillingOverview tabValue={tabValue}/>
        <BillingClients tabValue={tabValue}/>
        <BillingEntities tabValue={tabValue}/>
        <BillingRetainers tabValue={tabValue}/>
        <BillingInactiveClients tabValue={tabValue}/>
        <BillingRates tabValue={tabValue}/>
        <BillingCharges tabValue={tabValue}/>
        <BillingInvoices tabValue={tabValue}/>
        <BillingUnbilled tabValue={tabValue}/>
        <BillingFixedPricePayments tabValue={tabValue}/>
        <BillingFixedPriceSetupNeeded tabValue={tabValue}/>
        <BillingEmployees tabValue={tabValue}/>
      </TabContext>
      <NewInvoiceDialog
        open={newInvoiceDialogOpen}
        onClose={() => setNewInvoiceDialogOpen(false)}
      />
      <ActivitySummaryPicker
        open={isMonthPickerOpen}
        anchorEl={monthPickerAnchorEl}
        onClose={closeMonthPicker}
        title="Generate monthly invoices"
        helperText="Pick a month. Invoices will be generated for every billing entity with billable activity in that month."
        value={monthPickerValue}
        onChange={changeMonth}
        minDate={monthPickerBounds.min}
        maxDate={monthPickerBounds.max}
        views={['year', 'month']}
        openTo="month"
        format="MMMM YYYY"
        pickerLabel="Month"
        primaryLabel="Generate invoices"
        onSubmit={submitMonthPicker}
        submitDisabled={isGenerating || isDueDateInvalid || isInvoiceDateInvalid || isDueBeforeInvoice}
      >
        <DueDateSection>
          <DatePicker
            label="Invoice date"
            value={invoiceDateValue}
            onChange={(value) => setInvoiceDateValue(value)}
            format="MMMM D, YYYY"
            slotProps={{ textField: { fullWidth: true, size: "small", error: isInvoiceDateInvalid } }}
          />
          <DueDateHelper variant="caption">
            Defaults to the last day of the month being billed.
          </DueDateHelper>
        </DueDateSection>
        <DueDateSection>
          <DatePicker
            label="Due date (optional)"
            value={dueDateValue}
            minDate={earliestDueDate}
            onChange={(value) => setDueDateValue(value)}
            format="MMMM D, YYYY"
            slotProps={{
              textField: { fullWidth: true, size: "small" },
              field: { clearable: true, onClear: () => setDueDateValue(null) },
            }}
          />
          <DueDateHelper variant="caption">
            {isDueBeforeInvoice
              ? "The due date cannot be before the invoice date."
              : isDueDateSet && !isDueDateInvalid
                ? `Every invoice in this run will come due on ${dueDateValue.format("MMMM D, YYYY")}.`
                : "Leave this empty to use the default payment terms from billing settings. Setting a date pins the exact day these invoices come due; it must be today or later."}
          </DueDateHelper>
          {isDueDateSet && (
            <DueDateResetButton type="button" size="small" onClick={() => setDueDateValue(null)}>
              Use default payment terms
            </DueDateResetButton>
          )}
        </DueDateSection>
      </ActivitySummaryPicker>
    </ContainerBox>
  );
}
